import React, { Fragment, useMemo } from 'react';
import { useTranslation } from '@rbx/intl';
import { Typography, makeStyles } from '@rbx/ui';
import { stickyBreadcrumbScrollOffset } from '@modules/ui/shared/utils/styles';
import {
  ReferenceType,
  MemberType,
  APIReferenceProperty,
  APIReferenceConstructor,
  APIReferenceMethod,
  APIReferenceFunction,
  APIReferenceMathOperation,
  isMathOperationMember,
} from '@modules/client';
import {
  labelTranslKeyByMemberType,
  urlHashByMemberTypeDetailSection,
} from '@modules/apiReference/page/constants/apiReferenceConstants';
import { orderedMemberTypes } from '@modules/apiReference/page/utils/apiReferenceUtils';
import ReferenceDivider from '../../sharedUI/components/ReferenceDivider';
import { getVisibleReferenceTags } from '../../sharedUI/utils/tagUtils';
import { getUniqueMemberTitleHashId } from '../../utils/apiReferencePageNavUtils';
import ReferenceMemberDetailContent from '../../sharedUI/components/ReferenceMemberDetailContent';

const useDatatypeMemberDetailsContainerStyles = makeStyles()(() => ({
  sectionTitle: {
    marginBottom: '36px',
    scrollMarginTop: stickyBreadcrumbScrollOffset,
  },
  memberList: {
    marginBottom: '48px',
  },
}));

interface DatatypeMemberDetailsContainerProps {
  constructors: APIReferenceConstructor[] | null;
  properties: APIReferenceProperty[] | null;
  methods: APIReferenceMethod[] | null;
  functions: APIReferenceFunction[] | null;
  mathOperations: APIReferenceMathOperation[] | null;
}

type DatatypeMemberType =
  | MemberType.Constructor
  | MemberType.Property
  | MemberType.Method
  | MemberType.Function
  | MemberType.MathOperation;

const memberTypeToFieldName: Record<DatatypeMemberType, keyof DatatypeMemberDetailsContainerProps> =
  {
    [MemberType.Constructor]: 'constructors',
    [MemberType.Property]: 'properties',
    [MemberType.Method]: 'methods',
    [MemberType.Function]: 'functions',
    [MemberType.MathOperation]: 'mathOperations',
  };

function DatatypeMemberDetailsContainer({
  constructors,
  properties,
  methods,
  functions,
  mathOperations,
}: DatatypeMemberDetailsContainerProps) {
  const { translate } = useTranslation();
  const { classes } = useDatatypeMemberDetailsContainerStyles();

  const existingSectionsOrderedMemberTypes = useMemo(() => {
    const membersByType = {
      constructors,
      properties,
      methods,
      functions,
      mathOperations,
    };

    const datatypeMemberTypes = orderedMemberTypes(ReferenceType.DATATYPE) as DatatypeMemberType[];
    return datatypeMemberTypes.filter((memberType) => {
      const fieldName = memberTypeToFieldName[memberType];
      const membersList = membersByType[fieldName];
      return Array.isArray(membersList) && membersList.length > 0;
    });
  }, [constructors, properties, methods, functions, mathOperations]);

  if (existingSectionsOrderedMemberTypes.length === 0) return null;

  return (
    <Fragment>
      <ReferenceDivider />
      <Typography variant='h2' component='div' style={{ marginBottom: '24px' }}>
        {translate('Label.APIReference') || 'API Reference'}
      </Typography>
      {existingSectionsOrderedMemberTypes.map((memberType) => {
        const membersByType = {
          constructors,
          properties,
          methods,
          functions,
          mathOperations,
        };

        const headerTranslKey = labelTranslKeyByMemberType[memberType];
        const fieldName = memberTypeToFieldName[memberType];
        const sectionId = urlHashByMemberTypeDetailSection[memberType].slice(1);
        const members = membersByType[fieldName];

        return (
          <Fragment key={memberType}>
            <Typography
              id={sectionId}
              variant='largeLabel2'
              component='div'
              className={classes.sectionTitle}>
              {translate(headerTranslKey) || headerTranslKey}
            </Typography>
            <section className={classes.memberList}>
              {members!.map((member, i) => {
                const prevMember = members![i - 1];
                const isSameNameAsPrevMember =
                  isMathOperationMember(member) || !prevMember || isMathOperationMember(prevMember)
                    ? false
                    : member.name === prevMember.name;

                // Math operations use 'operation' field, others use 'name'
                const isMathOp = isMathOperationMember(member);
                const memberName = isMathOp
                  ? (member as APIReferenceMathOperation).operation
                  : member.name;

                const allTags = getVisibleReferenceTags({
                  tags: member.tags,
                });

                const id = getUniqueMemberTitleHashId(member, isSameNameAsPrevMember);
                const memberKey = id || `${memberName}-${memberType}`;

                return (
                  <ReferenceMemberDetailContent
                    key={memberKey}
                    name={memberName}
                    summary={member.summary}
                    allTags={allTags}
                    description={member.description}
                    deprecationMessage={member.deprecationMessage}
                    member={member}
                    memberType={memberType}
                    id={id}
                  />
                );
              })}
            </section>
          </Fragment>
        );
      })}
    </Fragment>
  );
}

export default DatatypeMemberDetailsContainer;
